import { FC } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";

import {
  ErrorDisplayUnsavedItems,
  ErrorsDisplayContainer,
} from "@ryde/controls";
import {
  useConfirmDirtyField,
  useProduceSummary,
  useYupValidationResolver,
} from "@ryde/redux";
import {
  buildProductionCriteriaCustomProduceButton,
  DrawerType,
  Log,
  parseBoolean,
  resolveProduceDirectionsServicePayload,
  useDrawers,
  type IDrawerCustomProductionProps,
  type IInjectedDrawerProps,
  type IProductionForm,
} from "@ryde/shared";

import {
  BaseDrawer,
  DrawerContentStyled,
  DrawerHeader,
  FormFooter,
} from "../drawer";
import { CustomProductionFormTemplate } from "./custom-production-form.template";
import { customProductionValidationSchema } from "./custom-production.validation";

export const DrawerCustomProduction: FC<
  IDrawerCustomProductionProps & IInjectedDrawerProps
> = ({ onCloseDrawer, drawerNumber }) => {
  const { t } = useTranslation();
  const { buildProduceSummaryForCustomProductionDialog } = useProduceSummary();
  const resolver = useYupValidationResolver(
    customProductionValidationSchema,
    "produceButton",
  );
  const { openAsyncDrawer } = useDrawers();

  const methods = useForm<IProductionForm>({
    mode: "all",
    reValidateMode: "onChange",
    criteriaMode: "all",
    resolver,
  });
  const {
    getValues,
    formState: { isValid, isDirty, errors },
    trigger,
  } = methods;

  const { applyIfConfirmed } = useConfirmDirtyField({
    customContent: <ErrorDisplayUnsavedItems />,
  });

  const onSaveClick = async (): Promise<void> => {
    const validForm = await trigger();

    if (!validForm) {
      return;
    }

    const customProductionFormData = getValues();

    if (
      !customProductionFormData.produceFrom ||
      !customProductionFormData.produceTo
    ) {
      Log.warnAndToast("rideProduction.selectProductionDates");
      return;
    }

    if (!customProductionFormData.productionAction) {
      Log.warnAndToast("rideProduction.selectProductionAction");
      return;
    }

    const criteria = buildProductionCriteriaCustomProduceButton(
      customProductionFormData,
    );

    const summaryItems = await buildProduceSummaryForCustomProductionDialog(
      customProductionFormData,
    );

    await openAsyncDrawer({
      drawerProps: {
        type: DrawerType.ProductionPreview,
        action: customProductionFormData.productionAction,
        productionType: customProductionFormData.productionType,
        rideTypeId: customProductionFormData.rideTypeId,
        assignDocksDuringProduction:
          customProductionFormData.assignDocksDuringProduction,
        doNotLockBookings: parseBoolean(
          customProductionFormData.doNotLockBookings,
        ),
        ...resolveProduceDirectionsServicePayload(
          customProductionFormData.directionsService,
        ),
        summaryItems,
        criteria,
        isCloseAfterProductionFinish: false,
      },
    });

    onCloseDrawer();
  };

  const onCloseHandler = applyIfConfirmed({ isDirty })((): void => {
    onCloseDrawer();
  });

  return (
    <BaseDrawer onCloseDrawer={onCloseHandler} drawerNumber={drawerNumber}>
      <DrawerHeader
        onCloseDrawer={onCloseHandler}
        title={t("produceButton.CUSTOM_PRODUCTION")}
      />
      <DrawerContentStyled className="drawer-form-with-image">
        <FormProvider {...methods}>
          <CustomProductionFormTemplate />
        </FormProvider>
        <ErrorsDisplayContainer formErrors={isValid ? undefined : errors} />
      </DrawerContentStyled>
      <FormFooter
        onSaveClick={onSaveClick}
        onCancelClick={onCloseHandler}
        saveLabel={t("general.PRODUCE")}
      />
    </BaseDrawer>
  );
};
